import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'

import type { UnitDetail } from '../../types/api'
import type { DateRange } from '../../components/calendar/AvailabilityCalendar'
import { bookingsApi, unitsApi } from '../../api/endpoints'
import { ApiError } from '../../api/client'
import { useFormatters } from '../../hooks/useFormatters'

interface Props {
  unit: UnitDetail
  range: DateRange
}

/**
 * Quote and request, in one panel.
 *
 * The quote is fetched from the server rather than multiplied in the browser. Nightly
 * overrides, the arrival night's minimum stay and the capacity check all live in the pricing
 * service, and a second implementation here would eventually disagree with it. The visitor
 * would then be shown one number and charged another, which is the kind of bug that ends up
 * in a review.
 */
export function BookingPanel({ unit, range }: Props) {
  const { t } = useTranslation()
  const { money, moneyPrecise, dateWithWeekday } = useFormatters()
  const queryClient = useQueryClient()

  const [adults, setAdults] = useState(2)
  const [children, setChildren] = useState(0)
  const [form, setForm] = useState({
    guestName: '',
    guestEmail: '',
    guestPhone: '',
    guestCountry: '',
    message: '',
    acceptedTerms: false,
  })

  const datesChosen = Boolean(range.checkIn && range.checkOut)

  const quote = useQuery({
    queryKey: ['quote', unit.slug, range.checkIn, range.checkOut, adults, children],
    queryFn: () => unitsApi.quote(unit.slug, range.checkIn!, range.checkOut!, adults, children),
    enabled: datesChosen,
    staleTime: 0,
  })

  const createBooking = useMutation({
    mutationFn: () =>
      bookingsApi.create(unit.slug, {
        checkIn: range.checkIn!,
        checkOut: range.checkOut!,
        adults,
        children,
        guestName: form.guestName,
        guestEmail: form.guestEmail,
        guestPhone: form.guestPhone || undefined,
        guestCountry: form.guestCountry || undefined,
        message: form.message || undefined,
        acceptedTerms: form.acceptedTerms,
      }),
    onSuccess: () => {
      // The nights this booking just took are now closed; drop the cached calendar so the
      // next visitor to this page cannot select them.
      void queryClient.invalidateQueries({ queryKey: ['availability', unit.slug] })
    },
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    createBooking.mutate()
  }

  const fieldErrors =
    createBooking.error instanceof ApiError ? createBooking.error.fieldErrors : {}

  // ---------------------------------------------------------------- success

  if (createBooking.isSuccess) {
    const booking = createBooking.data
    return (
      <Alert severity="success" sx={{ borderRadius: 3 }}>
        <AlertTitle>{t('booking.successTitle')}</AlertTitle>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {t('booking.successBody', { reference: booking.reference })}
        </Typography>
        <Typography
          variant="h5"
          sx={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '0.04em' }}
        >
          {booking.reference}
        </Typography>
      </Alert>
    )
  }

  // ---------------------------------------------------------------- form

  return (
    <Box component="form" onSubmit={submit} noValidate>
      <Stack spacing={2}>
        {/* Dates are picked on the calendar; these fields report the choice back. */}
        <Stack direction="row" spacing={1.5}>
          <ReadOnlyDate
            label={t('booking.checkIn')}
            value={range.checkIn ? dateWithWeekday(range.checkIn) : ''}
            placeholder={t('calendar.chooseCheckIn')}
          />
          <ReadOnlyDate
            label={t('booking.checkOut')}
            value={range.checkOut ? dateWithWeekday(range.checkOut) : ''}
            placeholder={t('calendar.chooseCheckOut')}
          />
        </Stack>

        <Stack direction="row" spacing={1.5}>
          <TextField
            select
            fullWidth
            size="small"
            label={t('common.adults')}
            value={adults}
            onChange={(event) => setAdults(Number(event.target.value))}
          >
            {Array.from({ length: unit.maxGuests }, (_, index) => index + 1).map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            size="small"
            label={t('common.children')}
            value={children}
            onChange={(event) => setChildren(Number(event.target.value))}
          >
            {Array.from({ length: Math.max(unit.maxGuests - adults + 1, 1) }, (_, index) => index).map(
              (n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ),
            )}
          </TextField>
        </Stack>

        {!datesChosen && (
          <Typography variant="body2" color="text.secondary">
            {t('booking.selectDatesFirst')}
          </Typography>
        )}

        {datesChosen && quote.isPending && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              {t('common.loading')}…
            </Typography>
          </Stack>
        )}

        {quote.data && !quote.data.available && (
          <Alert severity="warning" sx={{ borderRadius: 3 }}>
            <AlertTitle>{t('booking.unavailableTitle')}</AlertTitle>
            {quote.data.unavailableReason}
          </Alert>
        )}

        {quote.data?.available && (
          <>
            <Divider />
            <Stack spacing={0.75}>
              <PriceRow
                label={`${money(quote.data.averagePerNight)} × ${t('common.night', {
                  count: quote.data.nights,
                })}`}
                value={moneyPrecise(quote.data.accommodationTotal)}
              />
              <PriceRow label={t('booking.cleaningFee')} value={moneyPrecise(quote.data.cleaningFee)} />
              <Divider sx={{ my: 0.5 }} />
              <PriceRow label={t('booking.total')} value={moneyPrecise(quote.data.total)} strong />
            </Stack>
            <Divider />

            <TextField
              required
              size="small"
              label={t('booking.name')}
              value={form.guestName}
              onChange={(event) => setForm({ ...form, guestName: event.target.value })}
              error={Boolean(fieldErrors.guestName)}
              helperText={fieldErrors.guestName}
              autoComplete="name"
            />
            <TextField
              required
              size="small"
              type="email"
              label={t('booking.email')}
              value={form.guestEmail}
              onChange={(event) => setForm({ ...form, guestEmail: event.target.value })}
              error={Boolean(fieldErrors.guestEmail)}
              helperText={fieldErrors.guestEmail}
              autoComplete="email"
            />
            <TextField
              size="small"
              type="tel"
              label={t('booking.phone')}
              value={form.guestPhone}
              onChange={(event) => setForm({ ...form, guestPhone: event.target.value })}
              error={Boolean(fieldErrors.guestPhone)}
              helperText={fieldErrors.guestPhone}
              autoComplete="tel"
            />
            <TextField
              size="small"
              multiline
              minRows={2}
              label={t('booking.message')}
              placeholder={t('booking.messagePlaceholder')}
              value={form.message}
              onChange={(event) => setForm({ ...form, message: event.target.value })}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={form.acceptedTerms}
                  onChange={(event) => setForm({ ...form, acceptedTerms: event.target.checked })}
                />
              }
              label={<Typography variant="body2">{t('booking.acceptTerms')}</Typography>}
            />

            {createBooking.isError && (
              <Alert severity="error" sx={{ borderRadius: 3 }}>
                {createBooking.error instanceof ApiError
                  ? createBooking.error.message
                  : t('common.errorBody')}
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={createBooking.isPending || !form.acceptedTerms}
            >
              {createBooking.isPending ? t('booking.submitting') : t('booking.submit')}
            </Button>

            <Typography variant="caption" color="text.secondary">
              {t('booking.noPayment')}
            </Typography>
          </>
        )}
      </Stack>
    </Box>
  )
}

function ReadOnlyDate({
  label,
  value,
  placeholder,
}: {
  label: string
  value: string
  placeholder: string
}) {
  return (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={value}
      placeholder={placeholder}
      slotProps={{ input: { readOnly: true } }}
    />
  )
}

function PriceRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
      <Typography variant={strong ? 'subtitle1' : 'body2'} sx={{ fontWeight: strong ? 700 : 400 }}>
        {label}
      </Typography>
      <Typography
        variant={strong ? 'subtitle1' : 'body2'}
        sx={{ fontWeight: strong ? 700 : 500, fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </Typography>
    </Stack>
  )
}
